import { alpha } from '@mui/material/styles';
import { JLR_PLANT } from '../../constants';

export default function Button(theme) {
  const isJlr = theme.plantName === JLR_PLANT;
  const isDarkMode = theme.palette.mode === 'dark';

  const jlrButtonStyles = {
    fontFamily: "'Inter', sans-serif",
    fontWeight: 600,
    borderRadius: '16px',
    textTransform: 'none',
    letterSpacing: '0.5px',
    padding: '12px 24px',
    fontSize: '1rem',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    position: 'relative',
    overflow: 'hidden',
    minHeight: '48px',
    minWidth: '140px',
    boxShadow: isDarkMode 
      ? '0 4px 20px rgba(0, 0, 0, 0.3)'
      : '0 4px 20px rgba(0, 0, 0, 0.1)',
    
    // Shimmer effect
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: '-100%',
      width: '100%',
      height: '100%',
      background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
      transition: 'left 0.6s ease',
    },
    '&:hover::before': {
      left: '100%',
    },
    
    '&:hover': {
      transform: 'translateY(-2px) scale(1.02)',
      boxShadow: isDarkMode 
        ? '0 8px 40px rgba(0, 0, 0, 0.4)'
        : '0 8px 40px rgba(0, 0, 0, 0.2)',
    },
    
    '&:active': {
      transform: 'translateY(0) scale(1)',
    },
    
    '&.Mui-disabled': {
      opacity: 0.6,
      transform: 'none',
      boxShadow: 'none',
    },
  };

  const standardButtonStyles = {
    borderRadius: '8px',
    textTransform: 'none',
    fontWeight: 500,
    '&:hover': {
      transform: 'translateY(-1px)',
    },
  };

  const baseStyles = isJlr ? jlrButtonStyles : standardButtonStyles;

  return {
    MuiButton: {
      styleOverrides: {
        root: baseStyles,
        
        // Primary variant
        containedPrimary: {
          background: isJlr 
            ? (isDarkMode 
              ? 'linear-gradient(135deg, #0066cc, #004499)'
              : 'linear-gradient(135deg, #2d4a2d, #1a2d1a)')
            : theme.palette.primary.main,
          color: '#ffffff',
          border: 'none',
          '&:hover': {
            background: isJlr 
              ? (isDarkMode 
                ? 'linear-gradient(135deg, #004499, #002266)'
                : 'linear-gradient(135deg, #1a2d1a, #0f1a0f)')
              : theme.palette.primary.dark,
          },
        },

        // Secondary variant
        containedSecondary: {
          background: isJlr 
            ? (isDarkMode 
              ? 'linear-gradient(135deg, rgba(200, 185, 156, 0.2), rgba(157, 139, 111, 0.1))'
              : 'linear-gradient(135deg, #c8b99c, #9d8b6f)')
            : theme.palette.secondary.main,
          color: isJlr ? (isDarkMode ? '#ffffff' : '#2d4a2d') : '#ffffff',
          border: isJlr ? `1px solid ${isDarkMode ? 'rgba(200, 185, 156, 0.3)' : '#c8b99c'}` : 'none',
        },

        // Outlined variant
        outlined: {
          background: 'transparent',
          color: isJlr ? (isDarkMode ? '#ffffff' : '#2d4a2d') : theme.palette.text.primary,
          border: `1px solid ${isJlr 
            ? (isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(45, 74, 45, 0.2)')
            : theme.palette.divider}`,
          '&:hover': {
            background: isJlr 
              ? (isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(45, 74, 45, 0.05)')
              : 'rgba(0, 0, 0, 0.04)',
          },
        },

        // Text variant
        text: {
          color: isJlr ? (isDarkMode ? '#ffffff' : '#2d4a2d') : theme.palette.text.primary,
          '&:hover': {
            background: isJlr 
              ? (isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(45, 74, 45, 0.05)')
              : alpha(theme.palette.primary.main, 0.04),
          },
        },

        // Size variants
        sizeSmall: {
          padding: isJlr ? '8px 16px' : '6px 12px',
          fontSize: isJlr ? '0.875rem' : '0.8125rem',
          minHeight: isJlr ? '36px' : '32px',
          minWidth: isJlr ? '100px' : '80px',
        },

        sizeLarge: {
          padding: isJlr ? '16px 32px' : '12px 24px',
          fontSize: isJlr ? '1.125rem' : '1rem',
          minHeight: isJlr ? '56px' : '48px',
          minWidth: isJlr ? '180px' : '160px',
        },
      },
    },
  };
}
